import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { XCircle, CheckCircle2, LockKeyhole, KeyRound, CircleCheck } from "lucide-react";
import { PREVIEW_GRID, PRECEPTOR_QUICK } from "../../data/content";

export const StakeholderGrid = ({ onOpenAuth, onOpenDemo, onApplyPreceptor, onApplyTrack }) => (
  <section id="stakeholder-grid" className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="stakeholder-grid-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Stakeholder Pathways</p>
      <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0B1B2B] tracking-tight max-w-3xl">
        Seven Audiences. One Trusted Partner.
      </h2>
      <p className="mt-5 text-[#64748B] leading-relaxed text-sm md:text-base max-w-3xl">
        Find yourself below. Institutions see full program details after a quick verification step.
        Preceptors can see pay, requirements, and apply directly — no login needed.
      </p>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PREVIEW_GRID.map((card, idx) => {
          const C = Icons[card.icon] || Icons.Building;
          if (card.id === "preceptors") {
            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (idx % 3) * 0.08 }}
                className="flex flex-col bg-white border-2 border-[#D4AF37]/60 rounded-xl p-7 shadow-sm hover:shadow-xl transition-all duration-300"
                data-testid={`preview-card-${card.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[#0B1B2B] flex items-center justify-center">
                    <C size={20} className="text-[#D4AF37]" />
                  </div>
                  <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#D4AF37]">No login needed</span>
                </div>
                <h3 className="mt-5 font-serif text-xl text-[#0B1B2B] font-semibold leading-snug">{card.title}</h3>
                <div className="mt-5">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#94A3B8]">Typical Pay</div>
                  <p className="mt-1.5 font-serif text-lg text-[#0B1B2B] font-semibold" data-testid="preceptor-comp-range">{PRECEPTOR_QUICK.compRange}</p>
                  <p className="mt-1 text-xs text-[#64748B] leading-snug">{PRECEPTOR_QUICK.compNote}</p>
                </div>
                <div className="mt-5 flex-1">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#94A3B8]">To Qualify, You Need</div>
                  <ul className="mt-2.5 space-y-2">
                    {PRECEPTOR_QUICK.eligibility.map((e) => (
                      <li key={e} className="flex items-start gap-2 text-sm text-[#334155] leading-snug">
                        <CircleCheck size={14} className="text-[#D4AF37] mt-0.5 shrink-0" /> {e}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={onApplyPreceptor}
                  className="mt-7 w-full bg-[#D4AF37] text-[#0B1B2B] text-sm font-semibold py-3 rounded-xl hover:bg-[#e8c968] transition-colors flex items-center justify-center gap-2"
                  data-testid={`apply-pathway-${card.id}`}
                >
                  Apply to Precept
                </button>
                <button
                  onClick={onOpenAuth}
                  className="mt-2.5 text-xs text-[#64748B] hover:text-[#0B1B2B] font-medium transition-colors flex items-center justify-center gap-1.5 w-full"
                  data-testid={`preceptor-portal-login-${card.id}`}
                >
                  <LockKeyhole size={12} className="text-[#D4AF37]" /> or log in to the full Preceptor Portal
                </button>
              </motion.div>
            );
          }
          return (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (idx % 3) * 0.08 }}
              className="flex flex-col bg-white border border-[#E2E8F0] rounded-xl p-7 shadow-sm hover:border-[#D4AF37]/60 hover:shadow-xl transition-all duration-300"
              data-testid={`preview-card-${card.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#0B1B2B] flex items-center justify-center">
                  <C size={20} className="text-[#D4AF37]" />
                </div>
                <LockKeyhole size={15} className="text-[#94A3B8]" />
              </div>
              <h3 className="mt-5 font-serif text-xl text-[#0B1B2B] font-semibold leading-snug">{card.title}</h3>
              <div className="mt-5">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#94A3B8]">Pain Points Solved</div>
                <ul className="mt-2.5 space-y-2">
                  {card.pains.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-[#64748B] leading-snug">
                      <XCircle size={14} className="text-red-400 mt-0.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-5 flex-1">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#94A3B8]">Deliverables Waiting Inside</div>
                <ul className="mt-2.5 space-y-2">
                  {card.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-[#334155] leading-snug">
                      <CheckCircle2 size={14} className="text-[#D4AF37] mt-0.5 shrink-0" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => onApplyTrack(card.id)}
                className="mt-7 w-full bg-[#D4AF37] text-[#0B1B2B] text-sm font-semibold py-3 rounded-xl hover:bg-[#e8c968] transition-colors flex items-center justify-center gap-2"
                data-testid={`apply-pathway-${card.id}`}
              >
                Apply / Inquire Directly
              </button>
              <button
                onClick={onOpenAuth}
                className="mt-2.5 w-full bg-[#0B1B2B] text-white text-sm font-semibold py-3 rounded-xl hover:bg-[#152a40] transition-colors flex items-center justify-center gap-2"
                data-testid={`access-pathway-${card.id}`}
              >
                <LockKeyhole size={14} className="text-[#D4AF37]" /> Access This Pathway
              </button>
              <button
                onClick={onOpenDemo}
                className="mt-2.5 text-xs text-[#64748B] hover:text-[#0B1B2B] font-medium transition-colors flex items-center justify-center gap-1.5 w-full"
                data-testid={`request-demo-${card.id}`}
              >
                <KeyRound size={12} className="text-[#D4AF37]" /> or request demo credentials
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);
